import { Injectable, Injector, ProviderToken, inject } from '@angular/core';
import { GridPreferencesService } from '@compiere-ws/services/grid-preferences/grid-preferences.service';
import { LicenseService } from '@compiere-ws/services/license/license.service';
import { SocketService } from '@compiere-ws/services/socket/socket.service';
import { DataStoreService } from '@iupics-manager/managers/data-store/data-store.service';
import { UnsavedChangesService } from '@iupics-manager/managers/unsaved-changes/unsaved-changes.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { KeycloakService } from 'keycloak-angular';
import { Observable, Subject } from 'rxjs';

export type SessionResetReason = 'role' | 'logout';

/**
 * What must be forgotten when the session changes (role switch, logout), in one place.
 * The core caches are reset here, in order; other front caches listen to reset$ (or to
 * CacheManagerService.cachingDataReset$, emitted by the UICreator reset).
 * Notes: docs/frontend/README.md (session reset).
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  // services resolved when used, not injected: the HTTP interceptor uses this service and
  // UICreator / DataStore need HttpClient (no injection cycle)
  readonly #injector = inject(Injector);
  readonly #reset$ = new Subject<SessionResetReason>();
  /** after the core caches are reset */
  readonly reset$: Observable<SessionResetReason> = this.#reset$.asObservable();
  #loggingOut = false;

  get loggingOut(): boolean {
    return this.#loggingOut;
  }

  reset(reason: SessionResetReason): void {
    // UICreator also resets CacheManager (processes, product attributes, tags), dashboards and widgets
    this.#run(UICreatorService, (s) => s.resetCachingData());
    this.#run(DataStoreService, (s) => s.cleanDataStore());
    this.#run(LicenseService, (s) => s.resetCachingData());
    this.#run(GridPreferencesService, (s) => s.clearCache());
    this.#reset$.next(reason);
  }

  /**
   * Single logout: several 401 answers at once (parallel requests) or a click during a redirect log out
   * once. Caches and the socket are dropped before leaving.
   */
  logout(redirectUri: string = window.location.origin): void {
    if (this.#loggingOut) return;
    this.#loggingOut = true;
    // the redirect to Keycloak must not trigger the browser "leave the page?" question
    this.#run(UnsavedChangesService, (s) => s.allowUnload());
    this.reset('logout');
    this.#run(SocketService, (s) => s.disconnect());
    this.#injector
      .get(KeycloakService)
      .logout(redirectUri)
      .catch((e) => {
        // still here (logout refused): allow another try
        this.#loggingOut = false;
        console.error(e);
      });
  }

  /** a failing reset step must not stop the others (nor the logout) */
  #run<T>(token: ProviderToken<T>, fn: (service: T) => void): void {
    try {
      fn(this.#injector.get(token));
    } catch (e) {
      console.error(e);
    }
  }
}
